import { useCallback, useContext } from 'react';

import type {
	ClearReferrerCallback,
	RefreshSearchSessionIdCallback,
	SetQueryParamsCallback,
	SetThirdPartyConfigsCallback,
} from '../../../../../common/constants/bootstrap';
import { areAllEqualQueryParams, toQueryParamsRaw } from '../../../../../common/utils/query-params';
import { DynamicBootstrapContext, StaticBootstrapStoreContext } from '../index';
import { useStaticBootstrapSelector } from '../static-bootstrap-store';

// TODO QS-8575: (within the Bootstrap rss removal) This hook should be localised to the top of the render tree and passed down as a prop, with queryParams provided as an attribute. Currently whenever queryParams change, this will force a context search of the whole tree
export const useSetQueryParamsContext = (): SetQueryParamsCallback => {
	const { queryParams: currentQueryParams } = useContext(DynamicBootstrapContext);
	const hostProductSetQueryParamsCallback = useStaticBootstrapSelector(
		useContext(StaticBootstrapStoreContext),
		ctx => ctx.queryParamsCallback,
	);

	const setQueryParamsCallback: SetQueryParamsCallback = useCallback(newParams => {
		const areEqualQueryParams = areAllEqualQueryParams(
			newParams,
			currentQueryParams ?? {},
		);
		if (areEqualQueryParams) {
			return;
		}
		const rawQueryParams = toQueryParamsRaw(newParams);
		hostProductSetQueryParamsCallback(rawQueryParams);
	}, [currentQueryParams, hostProductSetQueryParamsCallback]);

	return setQueryParamsCallback;
};

/**
 * Refresh the search session ID by calling the host product callback, modifies the store.
 */
export const useRefreshSearchSessionIdContext = (): RefreshSearchSessionIdCallback => {
	const refreshSearchSessionId = useStaticBootstrapSelector(
		useContext(StaticBootstrapStoreContext),
		ctx => ctx.refreshSearchSessionId,
	);
	return refreshSearchSessionId;
};

/**
 * Clear the referrer by calling the host product callback, modifies the store.
 */
export const useClearReferrerContext = (): ClearReferrerCallback => {
	const clearReferrer = useStaticBootstrapSelector(
		useContext(StaticBootstrapStoreContext),
		ctx => ctx.clearReferrer,
	);
	return clearReferrer;
};

/**
 * Set third party configs by calling the host product callback, modifies the store.
 */
export const useSetThirdPartyConfigsContext = (): SetThirdPartyConfigsCallback => {
	const setThirdPartyConfigs = useStaticBootstrapSelector(
		useContext(StaticBootstrapStoreContext),
		ctx => ctx.setThirdPartyConfigs,
	);
	return setThirdPartyConfigs;
};
